import { useEffect } from 'react';
import { differenceInDays, startOfToday, isFuture } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from '@uidotdev/usehooks';

import { getArrayRandomValue } from '@phoenix/core/frontend/shared/util/arrays';
import { STATIC_ASSETS_CACHE_KEY } from '@phoenix/core/frontend/shared/util/assets/assets-key';
import { Asset } from '@phoenix/core/frontend/shared/domain/static-content/static-assets-api.model';

const IMAGES_TAG_ID = 'heroDashboard';
const CACHE_EXPIRATION_DAYS = 3;

export const useHeroImage = (): Asset | undefined => {
  const assets = (useQueryClient().getQueryData([STATIC_ASSETS_CACHE_KEY]) as Asset[] | undefined)?.filter((asset) =>
    asset.contentfulMetadata?.tags?.some(({ id }) => id === IMAGES_TAG_ID)
  );

  const today = startOfToday().getTime();
  const [cached, setCache] = useLocalStorage<(Asset & { time: number }) | undefined>(
    IMAGES_TAG_ID,
    assets?.length ? { ...getArrayRandomValue(assets), time: today } : undefined
  );

  const isCacheExpired = cached && differenceInDays(today, cached.time) >= CACHE_EXPIRATION_DAYS;
  const isCacheError =
    cached && !(cached.time && !isFuture(cached.time) && (assets as Asset[])?.some(({ fileName }) => fileName === cached.fileName));

  const purge = () => setCache({ ...getArrayRandomValue((assets as Asset[]).filter((a) => a.fileName !== cached?.fileName)), time: today });

  useEffect(() => {
    if (assets && (isCacheExpired || isCacheError)) {
      purge();
    }
  }, [cached, assets]);

  return !isCacheExpired && !isCacheError ? cached : undefined;
};
